import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="post-harvest--packaging"
// Manages bag-line packaging entries with live allocation validation.
export default class extends Controller {
  static targets = [
    "lineList",
    "lineTemplate",
    "packagingLinesDataField",
    "totalAvailable",
    "totalAllocated",
    "totalDifference",
    "allocationBadge",
    "qualitySummary",
  ];
  static values = {
    packagingTypes: Array,
    availableByQuality: Object,
    toleranceG: Number,
    pendingLabel: String,
    matchedLabel: String,
    mismatchLabel: String,
    qualityUnknownLabel: String,
    availableLabel: String,
    allocatedLabel: String,
    differenceLabel: String,
    weightExceedsMaxLabel: String,
    maxWeightHintLabel: String,
  };

  static UNIT_TO_GRAMS = {
    g: 1,
    kg: 1000,
    lb: 453.59237,
    oz: 28.349523125,
  };

  connect() {
    if (this.lineRows.length === 0) {
      this.addLine();
      return;
    }

    this.lineRows.forEach((row) => this.initializeWeightMax(row));
    this.update();
  }

  addLine() {
    if (!this.hasLineTemplateTarget) {
      return;
    }

    const uniqueToken = `${Date.now()}`;
    const rowHtml = this.lineTemplateTarget.innerHTML.replaceAll(
      "NEW_ID",
      uniqueToken,
    );
    this.lineListTarget.insertAdjacentHTML("beforeend", rowHtml);
    this.update();
  }

  removeLine(event) {
    const row = event.currentTarget.closest("[data-line-row]");
    if (row) {
      row.remove();
    }

    if (this.lineRows.length === 0) {
      this.addLine();
      return;
    }

    this.update();
  }

  packagingTypeChanged(event) {
    const row = event.currentTarget.closest("[data-line-row]");
    if (!row) {
      return;
    }

    const packagingUnitTypeId = this.parseInteger(event.currentTarget.value);
    if (packagingUnitTypeId === null) {
      delete row.dataset.maxWeightValue;
      delete row.dataset.maxWeightUnit;
      this.clearWeightMax(row);
      this.update();
      return;
    }

    const selectedPackagingType = this.packagingTypesValue.find(
      (packagingType) => {
        return Number.parseInt(packagingType.id, 10) === packagingUnitTypeId;
      },
    );
    if (!selectedPackagingType) {
      return;
    }

    const weightInput = row.querySelector("[data-field=weight_per_unit_value]");
    const weightUnitSelect = row.querySelector(
      "[data-field=weight_per_unit_unit]",
    );
    if (weightInput && weightInput.value === "") {
      weightInput.value = this.formatNumber(selectedPackagingType.weight_value);
      if (weightUnitSelect) {
        weightUnitSelect.value = selectedPackagingType.weight_unit || "g";
      }
    }

    row.dataset.maxWeightValue = selectedPackagingType.weight_value;
    row.dataset.maxWeightUnit = selectedPackagingType.weight_unit || "g";
    this.updateWeightMax(row);
    this.update();
  }

  update() {
    const lines = this.collectLines();
    this.renderLineTotals(lines);
    this.renderSummary(lines);
    this.serialize(lines);
    this.validateAllWeightMaxes();
  }

  collectLines() {
    return this.lineRows.map((row) => {
      const packagingUnitTypeRawValue = row.querySelector(
        "[data-field=packaging_unit_type_id]",
      )?.value;
      const weightPerUnitRawValue = row.querySelector(
        "[data-field=weight_per_unit_value]",
      )?.value;
      const weightPerUnitUnitValue =
        row.querySelector("[data-field=weight_per_unit_unit]")?.value || "";
      const quantityRawValue = row.querySelector(
        "[data-field=quantity]",
      )?.value;
      const cropQualityRawValue = row.querySelector(
        "[data-field=crop_quality_id]",
      )?.value;
      const packagingPhotoRawValue =
        row.querySelector("[data-post-harvest--photo-upload-target='signedId']")
          ?.value || "";

      return {
        row,
        packagingUnitTypeId: this.parseInteger(packagingUnitTypeRawValue),
        weightPerUnitValue: this.parseDecimal(weightPerUnitRawValue),
        weightPerUnitUnit: weightPerUnitUnitValue,
        quantity: this.parseInteger(quantityRawValue),
        cropQualityId: this.parseInteger(cropQualityRawValue),
        packagingPhotoSignedId: packagingPhotoRawValue,
        hasData: this.lineHasData({
          packagingUnitTypeRawValue,
          weightPerUnitRawValue,
          weightPerUnitUnitValue,
          quantityRawValue,
          cropQualityRawValue,
          packagingPhotoRawValue,
        }),
      };
    });
  }

  renderLineTotals(lines) {
    lines.forEach((line) => {
      const lineTotalElement = line.row.querySelector("[data-line-total]");
      if (!lineTotalElement) {
        return;
      }

      if (this.lineHasComputableTotal(line)) {
        const totalWeight = line.weightPerUnitValue * line.quantity;
        const displayUnit = line.weightPerUnitUnit || "g";
        lineTotalElement.textContent = `${this.formatNumber(totalWeight)} ${displayUnit}`;
      } else {
        lineTotalElement.textContent = "—";
      }
    });
  }

  renderSummary(lines) {
    const availableByQuality = this.availableByQualityValue || {};
    const allocatedByQuality = {};
    let allocatedTotalWeightInGrams = 0;

    lines.forEach((line) => {
      if (!this.lineHasComputableTotal(line)) {
        return;
      }

      const lineWeightInGrams = this.convertToGrams(
        line.weightPerUnitValue * line.quantity,
        line.weightPerUnitUnit,
      );
      allocatedTotalWeightInGrams += lineWeightInGrams;

      if (line.cropQualityId !== null) {
        const qualityKey = String(line.cropQualityId);
        allocatedByQuality[qualityKey] =
          (allocatedByQuality[qualityKey] || 0) + lineWeightInGrams;
      }
    });

    const availableTotalWeightInGrams = Object.values(
      availableByQuality,
    ).reduce((sum, qualityData) => {
      const availableWeight = Number.parseFloat(
        qualityData.available_weight_g || 0,
      );
      return sum + availableWeight;
    }, 0);
    const totalDifferenceInGrams =
      allocatedTotalWeightInGrams - availableTotalWeightInGrams;

    this.totalAvailableTarget.textContent = `${this.formatNumber(availableTotalWeightInGrams)} g`;
    this.totalAllocatedTarget.textContent = `${this.formatNumber(allocatedTotalWeightInGrams)} g`;
    this.totalDifferenceTarget.textContent = `${this.formatSignedNumber(totalDifferenceInGrams)} g`;

    const comparableQualityKeys = Array.from(
      new Set([
        ...Object.keys(availableByQuality),
        ...Object.keys(allocatedByQuality),
      ]),
    );
    const toleranceInGrams = this.hasToleranceGValue
      ? this.toleranceGValue
      : 0.01;
    const totalMatches = Math.abs(totalDifferenceInGrams) <= toleranceInGrams;
    const qualityMatches = comparableQualityKeys.every((qualityKey) => {
      const availableWeight = Number.parseFloat(
        availableByQuality[qualityKey]?.available_weight_g || 0,
      );
      const allocatedWeight = allocatedByQuality[qualityKey] || 0;
      return Math.abs(allocatedWeight - availableWeight) <= toleranceInGrams;
    });

    const hasAnyLineData = lines.some((line) => line.hasData);
    const allocationsMatch = hasAnyLineData && totalMatches && qualityMatches;

    this.renderAllocationBadge(allocationsMatch, hasAnyLineData);
    this.renderQualitySummary(
      comparableQualityKeys,
      availableByQuality,
      allocatedByQuality,
      toleranceInGrams,
    );
  }

  renderAllocationBadge(allocationsMatch, hasAnyLineData) {
    this.allocationBadgeTarget.classList.remove(
      "badge-success",
      "badge-error",
      "badge-warning",
    );

    if (!hasAnyLineData) {
      this.allocationBadgeTarget.classList.add("badge-warning");
      this.allocationBadgeTarget.textContent = this.pendingLabelValue;
      return;
    }

    if (allocationsMatch) {
      this.allocationBadgeTarget.classList.add("badge-success");
      this.allocationBadgeTarget.textContent = this.matchedLabelValue;
    } else {
      this.allocationBadgeTarget.classList.add("badge-error");
      this.allocationBadgeTarget.textContent = this.mismatchLabelValue;
    }
  }

  renderQualitySummary(
    comparableQualityKeys,
    availableByQuality,
    allocatedByQuality,
    toleranceInGrams,
  ) {
    this.qualitySummaryTarget.innerHTML = "";
    const sortedQualityKeys = comparableQualityKeys.sort(
      (leftKey, rightKey) => {
        const leftName =
          availableByQuality[leftKey]?.name ||
          `${this.qualityUnknownLabelValue} ${leftKey}`;
        const rightName =
          availableByQuality[rightKey]?.name ||
          `${this.qualityUnknownLabelValue} ${rightKey}`;
        return leftName.localeCompare(rightName);
      },
    );

    sortedQualityKeys.forEach((qualityKey) => {
      const qualityData = availableByQuality[qualityKey];
      const qualityName =
        qualityData?.name || `${this.qualityUnknownLabelValue} ${qualityKey}`;
      const availableWeightInGrams = Number.parseFloat(
        qualityData?.available_weight_g || 0,
      );
      const allocatedWeightInGrams = allocatedByQuality[qualityKey] || 0;
      const qualityDifference = allocatedWeightInGrams - availableWeightInGrams;

      const rowElement = document.createElement("div");
      rowElement.className = "flex flex-wrap gap-3 items-center";

      const qualityNameElement = document.createElement("span");
      qualityNameElement.className = "font-medium";
      qualityNameElement.textContent = qualityName;
      rowElement.appendChild(qualityNameElement);

      const availableElement = document.createElement("span");
      availableElement.className = "text-base-content/70";
      availableElement.textContent = `${this.availableLabelValue}: ${this.formatNumber(availableWeightInGrams)} g`;
      rowElement.appendChild(availableElement);

      const allocatedElement = document.createElement("span");
      allocatedElement.className = "text-base-content/70";
      allocatedElement.textContent = `${this.allocatedLabelValue}: ${this.formatNumber(allocatedWeightInGrams)} g`;
      rowElement.appendChild(allocatedElement);

      const differenceElement = document.createElement("span");
      differenceElement.className =
        Math.abs(qualityDifference) <= toleranceInGrams
          ? "text-success"
          : "text-error";
      differenceElement.textContent = `${this.differenceLabelValue}: ${this.formatSignedNumber(qualityDifference)} g`;
      rowElement.appendChild(differenceElement);

      this.qualitySummaryTarget.appendChild(rowElement);
    });
  }

  serialize(lines) {
    const serializedLines = lines
      .filter((line) => line.hasData)
      .map((line) => {
        return {
          packaging_unit_type_id: line.packagingUnitTypeId,
          weight_per_unit_value: line.weightPerUnitValue,
          weight_per_unit_unit: line.weightPerUnitUnit || null,
          quantity: line.quantity,
          crop_quality_id: line.cropQualityId,
          packaging_photo_signed_id: line.packagingPhotoSignedId || null,
        };
      });

    this.packagingLinesDataFieldTarget.value = JSON.stringify(serializedLines);
  }

  lineHasData({
    packagingUnitTypeRawValue,
    weightPerUnitRawValue,
    weightPerUnitUnitValue,
    quantityRawValue,
    cropQualityRawValue,
    packagingPhotoRawValue,
  }) {
    return [
      packagingUnitTypeRawValue,
      weightPerUnitRawValue,
      weightPerUnitUnitValue,
      quantityRawValue,
      cropQualityRawValue,
      packagingPhotoRawValue,
    ].some((value) => {
      return String(value || "").trim() !== "";
    });
  }

  lineHasComputableTotal(line) {
    return (
      line.packagingUnitTypeId !== null &&
      line.cropQualityId !== null &&
      line.weightPerUnitValue !== null &&
      line.weightPerUnitValue > 0 &&
      line.weightPerUnitUnit !== "" &&
      line.quantity !== null &&
      line.quantity > 0
    );
  }

  convertToGrams(weightValue, unit) {
    const conversionFactor = this.constructor.UNIT_TO_GRAMS[unit] || 1;
    return weightValue * conversionFactor;
  }

  parseInteger(value) {
    if (value === undefined || value === null || String(value).trim() === "") {
      return null;
    }
    const parsedValue = Number.parseInt(value, 10);
    if (Number.isNaN(parsedValue)) {
      return null;
    }
    return parsedValue;
  }

  parseDecimal(value) {
    if (value === undefined || value === null || String(value).trim() === "") {
      return null;
    }
    const parsedValue = Number.parseFloat(value);
    if (Number.isNaN(parsedValue)) {
      return null;
    }
    return parsedValue;
  }

  formatNumber(value) {
    return Number.parseFloat(value || 0).toFixed(2);
  }

  formatSignedNumber(value) {
    const normalizedValue = Number.parseFloat(value || 0);
    const sign = normalizedValue > 0 ? "+" : "";
    return `${sign}${normalizedValue.toFixed(2)}`;
  }

  initializeWeightMax(row) {
    const typeSelect = row.querySelector("[data-field=packaging_unit_type_id]");
    if (!typeSelect) {
      return;
    }

    const packagingUnitTypeId = this.parseInteger(typeSelect.value);
    if (packagingUnitTypeId === null) {
      return;
    }

    const selectedPackagingType = this.packagingTypesValue.find((pt) => {
      return Number.parseInt(pt.id, 10) === packagingUnitTypeId;
    });
    if (!selectedPackagingType) {
      return;
    }

    row.dataset.maxWeightValue = selectedPackagingType.weight_value;
    row.dataset.maxWeightUnit = selectedPackagingType.weight_unit || "g";
    this.updateWeightMax(row);
  }

  updateWeightMax(row) {
    const maxWeightValue = this.parseDecimal(row.dataset.maxWeightValue);
    const maxWeightUnit = row.dataset.maxWeightUnit;
    const weightInput = row.querySelector("[data-field=weight_per_unit_value]");
    const weightUnitSelect = row.querySelector(
      "[data-field=weight_per_unit_unit]",
    );
    const maxDisplay = row.querySelector("[data-max-weight-display]");

    if (maxWeightValue === null || !maxWeightUnit || !weightInput) {
      this.clearWeightMax(row);
      return;
    }

    if (maxDisplay && this.maxWeightHintLabelValue) {
      const hintText = this.maxWeightHintLabelValue.replace(
        "%{max}",
        `${this.formatNumber(maxWeightValue)} ${maxWeightUnit}`,
      );
      maxDisplay.textContent = hintText;
      maxDisplay.classList.remove("hidden");
    }

    const currentUnit = weightUnitSelect?.value || "g";
    const maxInGrams = this.convertToGrams(maxWeightValue, maxWeightUnit);
    const conversionFactor = this.constructor.UNIT_TO_GRAMS[currentUnit] || 1;
    const maxInCurrentUnit = maxInGrams / conversionFactor;

    weightInput.max = maxInCurrentUnit;
    this.validateWeightExceedsMax(row);
  }

  validateWeightExceedsMax(row) {
    const weightInput = row.querySelector("[data-field=weight_per_unit_value]");
    const warning = row.querySelector("[data-weight-warning]");
    if (!weightInput || !warning) {
      return;
    }

    const enteredValue = this.parseDecimal(weightInput.value);
    const maxValue = this.parseDecimal(weightInput.max);

    if (enteredValue !== null && maxValue !== null && enteredValue > maxValue) {
      weightInput.classList.add("input-error");
      const maxWeightValue = this.parseDecimal(row.dataset.maxWeightValue);
      const maxWeightUnit = row.dataset.maxWeightUnit || "g";
      const maxDisplay = `${this.formatNumber(maxWeightValue)} ${maxWeightUnit}`;
      warning.textContent = this.weightExceedsMaxLabelValue.replace(
        "%{max}",
        maxDisplay,
      );
      warning.classList.remove("hidden");
    } else {
      weightInput.classList.remove("input-error");
      warning.classList.add("hidden");
    }
  }

  clearWeightMax(row) {
    const weightInput = row.querySelector("[data-field=weight_per_unit_value]");
    if (weightInput) {
      weightInput.removeAttribute("max");
      weightInput.classList.remove("input-error");
    }
    const warning = row.querySelector("[data-weight-warning]");
    if (warning) {
      warning.classList.add("hidden");
    }
    const maxDisplay = row.querySelector("[data-max-weight-display]");
    if (maxDisplay) {
      maxDisplay.classList.add("hidden");
    }
  }

  validateAllWeightMaxes() {
    this.lineRows.forEach((row) => {
      if (row.dataset.maxWeightValue) {
        this.updateWeightMax(row);
      }
    });
  }

  get lineRows() {
    return Array.from(this.lineListTarget.querySelectorAll("[data-line-row]"));
  }
}
